/**
 * [EN] The numbers the renewed consumer renders a banner card with, copied as plain values.
 *      closet-admin-web does not carry the aurora design system (`@clovirtualfashion/foundation`)
 *      that closet-public-web resolves these from, and adding it for one preview would pull a
 *      second theme into an MUI v4 app. So they are literals here, each traceable to its source —
 *      when the consumer moves, this file has to be edited, and that is the point of the trail.
 * [KO] 리뉴얼된 소비자가 배너 카드를 그릴 때 쓰는 수치를 값으로 옮겨 적은 것이다.
 *      closet-admin-web 에는 closet-public-web 이 이 값들을 꺼내는 aurora 디자인 시스템
 *      (`@clovirtualfashion/foundation`)이 없고, 프리뷰 하나 때문에 들이면 MUI v4 앱에 테마가
 *      둘이 된다. 그래서 리터럴로 두고 각 값의 출처를 적었다 — 소비자가 움직이면 이 파일을 고쳐야
 *      하고, 흔적을 남기는 이유가 그것이다.
 */

/** `SURFACE_RADIUS` (aurora `radius.level5`) */
export const BANNER_CARD_RADIUS = 16;

/** `BannerFace` 의 `BottomDim` — `Grayscale/Opacity/Fixed/BLevel5` 는 `rgba(0, 0, 0, 0.5)` 다 */
export const BANNER_DIM_GRADIENT =
  'linear-gradient(to top, rgba(0, 0, 0, 0.5) 28%, transparent 93%)';

/** 이미지가 아직 없을 때 카드가 드는 면 — aurora `Grayscale/Solid/Fixed/G8` */
export const BANNER_EMPTY_BACKGROUND = '#27262b';

/**
 * [EN] The surface the card sits on, matching the main page's own (`Grayscale/Solid/G0` on dark).
 *      Admin pages are white, and a white-text banner on white reads as broken until the image
 *      arrives.
 * [KO] 카드가 놓이는 면 — 메인이 쓰는 면과 같다 (다크의 `Grayscale/Solid/G0`). 어드민 페이지는
 *      흰색이라, 흰 글자 배너를 흰 바닥에 두면 이미지가 오기 전까지 깨진 것처럼 보인다.
 */
export const BANNER_STAGE_BACKGROUND = '#121214';

/**
 * [EN] `Grayscale/Solid/Fixed/White` and `.../Fixed/Black` — the consumer's `BannerFace` picks
 *      black for a `Light` banner and white otherwise, and drops the bottom dim under black copy.
 * [KO] `Grayscale/Solid/Fixed/White` 와 `.../Fixed/Black` 이다. 소비자의 `BannerFace` 는 `Light`
 *      배너면 검정, 아니면 흰색을 쓰고, 검정 글자 아래에서는 하단 딤을 그리지 않는다.
 */
export const BANNER_TEXT_COLORS = {
  WHITE: '#ffffff',
  BLACK: '#000000',
};

/** `BANNER_CENTER_SCALE` — from 768 up the consumer enlarges the centred card by this factor */
export const BANNER_CENTER_SCALE = 1.12;

export interface BannerPreviewSize {
  /** 미리 볼 화면 폭 — 프리셋의 이름이기도 하다 */
  viewportWidth: number;
  label: string;
  cardWidth: number;
  cardHeight: number;
  subtitleFontSize: number;
  titleFontSize: number;
  /** `TitleText` 의 행간 — 768 미만은 1.3, 이상은 1.7 */
  titleLineHeight: number;
  titleMaxLines: number;
  /** `BottomDim` 의 키 — 768 미만은 카드에 대한 비율이다 */
  dimHeight: string;
  textAreaPadding: string;
  /** 가운데 카드 확대 배율 — 768 미만은 확대가 없어 1 이다 */
  centerScale: number;
}

/**
 * [EN] Four presets, one per regime the consumer actually has — the card's width comes from the
 *      page's content width and its type steps at 480 and 768, so a single slider would lie about
 *      both. Each row is that regime's card size worked out from `TopBannerStrip`:
 *      `CONTENT_WIDTH = min(1400, 100vw − 80, 33.3333vw + 760)`, card = `CONTENT_WIDTH / 2 − 8`
 *      above 768 at 2.225 : 1, and `min(608, 100vw − 48)` at 2.375 : 1 below it with a 200px floor.
 *
 *      1200 is the tightest desktop card (552px), which is where the 32 / 49 character limits were
 *      measured — it is the row to trust when text looks close to the edge.
 * [KO] 프리셋 넷은 소비자가 실제로 가진 구간 하나씩이다. 카드 폭은 페이지 본문 폭에서 나오고 글자
 *      크기는 480 · 768 에서 단이 바뀌므로, 슬라이더 하나로는 둘 다 거짓이 된다. 각 줄은
 *      `TopBannerStrip` 에서 푼 그 구간의 카드 크기다:
 *      `CONTENT_WIDTH = min(1400, 100vw − 80, 33.3333vw + 760)`, 768 이상은 카드가
 *      `CONTENT_WIDTH / 2 − 8` 에 2.225 : 1, 그 아래는 `min(608, 100vw − 48)` 에 2.375 : 1 이고
 *      200px 바닥이 있다.
 *
 *      1200 이 가장 좁은 데스크탑 카드(552px)이고, 32 / 49 자 상한을 잰 자리가 거기다 — 문구가
 *      자리에 빠듯해 보일 때 믿을 줄이다.
 */
export const BANNER_PREVIEW_SIZES: BannerPreviewSize[] = [
  {
    viewportWidth: 1920,
    label: 'Desktop 1920',
    cardWidth: 692,
    cardHeight: 311,
    subtitleFontSize: 16,
    titleFontSize: 24,
    titleLineHeight: 1.7,
    titleMaxLines: 1,
    dimHeight: '174px',
    textAreaPadding: '32px 40px',
    centerScale: BANNER_CENTER_SCALE,
  },
  {
    viewportWidth: 1200,
    label: 'Laptop 1200',
    cardWidth: 552,
    cardHeight: 248,
    subtitleFontSize: 16,
    titleFontSize: 24,
    titleLineHeight: 1.7,
    titleMaxLines: 1,
    dimHeight: '174px',
    textAreaPadding: '32px 40px',
    centerScale: BANNER_CENTER_SCALE,
  },
  {
    viewportWidth: 600,
    label: 'Tablet 600',
    cardWidth: 552,
    cardHeight: 232,
    subtitleFontSize: 16,
    titleFontSize: 24,
    titleLineHeight: 1.3,
    titleMaxLines: 2,
    dimHeight: '60%',
    textAreaPadding: '32px 40px',
    centerScale: 1,
  },
  {
    viewportWidth: 360,
    label: 'Phone 360',
    cardWidth: 312,
    cardHeight: 200,
    subtitleFontSize: 14,
    titleFontSize: 20,
    titleLineHeight: 1.3,
    titleMaxLines: 2,
    dimHeight: '60%',
    textAreaPadding: '0 40px 20px',
    centerScale: 1,
  },
];

/**
 * [EN] The list's thumbnail is the widest preset scaled down, so the row and the form's preview
 *      cannot disagree about what a banner looks like. 300px keeps the column at the width the
 *      old two-image thumbnail had.
 * [KO] 목록 썸네일은 가장 넓은 프리셋을 줄인 것이다. 행과 폼 프리뷰가 배너의 모양을 두고 서로 다른
 *      말을 할 수 없게. 300px 은 예전 두 장짜리 썸네일이 쓰던 컬럼 폭을 그대로 지킨다.
 */
export const BANNER_THUMBNAIL_WIDTH = 300;
export const [BANNER_THUMBNAIL_BASE_SIZE] = BANNER_PREVIEW_SIZES;
